import { get, post } from "@/services/axiosUitils";
import type {
  GoogleHealthDayRow,
  GoogleHealthPayload,
} from "../types/googleHealthBridge.types";
import { GOOGLE_HEALTH_STORAGE_KEY } from "../types/googleHealthBridge.types";
import type { HealthSyncApiResponse } from "../utils/healthSyncMapper";
import { getGluceraUserIdFromStorage } from "../utils/gluceraAuthCookies";
import { getLocalDateString } from "../utils/homePageUtils";

export const HEALTH_SYNC_API_PATH = "/food_scanner/health-sync/" as const;
export const HEALTH_SYNC_API_CACHE_KEY = "glucera_health_sync_api_cache";

const healthSyncRequestConfig = () => ({
  showNotification: false as const,
  headers: {
    "X-User-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
  },
});

const cacheKeyForDate = (date: string) => `${HEALTH_SYNC_API_CACHE_KEY}:${date}`;

const healthSyncMatchesDate = (
  payload: HealthSyncApiResponse | null | undefined,
  date: string,
): boolean => {
  if (!payload) return false;
  return (
    payload.date_local === date ||
    payload.selected_date === date ||
    payload.today_local === date ||
    payload.today_overview?.date === date
  );
};

/** POST /food_scanner/health-sync/ body (matches backend contract). */
export interface GoogleHealthSyncRequest {
  user_id: string;
  health_data: GoogleHealthPayload["health_data"];
}

export type HealthSyncGoals = {
  steps_goal?: number;
  calories_goal?: number;
};

/** Merge steps_goal / calories_goal into health_data rows (request date when set). */
const enrichHealthDataWithGoals = (
  rows: GoogleHealthPayload["health_data"],
  goals?: HealthSyncGoals,
  date?: string,
): GoogleHealthPayload["health_data"] => {
  if (!goals) return rows;

  const goalFields: Pick<GoogleHealthDayRow, "steps_goal" | "calories_goal"> = {};
  if (goals.steps_goal != null && goals.steps_goal > 0) {
    goalFields.steps_goal = Math.round(goals.steps_goal);
  }
  if (goals.calories_goal != null && goals.calories_goal > 0) {
    goalFields.calories_goal = Math.round(goals.calories_goal);
  }
  if (!Object.keys(goalFields).length) return rows;

  const applyToRow = (row: GoogleHealthDayRow): GoogleHealthDayRow => ({
    ...row,
    ...goalFields,
  });

  if (!date) return rows.map(applyToRow);

  let matched = false;
  const next = rows.map((row) => {
    if (row.date !== date) return row;
    matched = true;
    return applyToRow(row);
  });

  // No row for this date yet — append a goals-bearing row so POST still lands.
  if (!matched) {
    next.push(
      applyToRow({
        date,
        steps: 0,
        calories: 0,
        distance: 0,
        sleep_minutes: 0,
        spo2: null,
        hrv: null,
        breathing_rate: null,
        skin_temperature: null,
      }),
    );
  }
  return next;
};

const resolveUserId = (payload: GoogleHealthPayload): string | null => {
  if (payload.user_id != null && String(payload.user_id).trim()) {
    return String(payload.user_id).trim();
  }
  return getGluceraUserIdFromStorage();
};

class GoogleHealthService {
  private static instance: GoogleHealthService;

  private constructor() {}

  public static getInstance(): GoogleHealthService {
    if (!GoogleHealthService.instance) {
      GoogleHealthService.instance = new GoogleHealthService();
    }
    return GoogleHealthService.instance;
  }

  loadCachedHealthSync(date?: string): HealthSyncApiResponse | null {
    const requestDate = date || getLocalDateString();
    try {
      const dated = localStorage.getItem(cacheKeyForDate(requestDate));
      if (dated) {
        const parsed = JSON.parse(dated) as HealthSyncApiResponse;
        if (healthSyncMatchesDate(parsed, requestDate)) return parsed;
      }
      // Legacy unscoped cache — only reuse when it belongs to this local day.
      const legacy = localStorage.getItem(HEALTH_SYNC_API_CACHE_KEY);
      if (!legacy) return null;
      const parsed = JSON.parse(legacy) as HealthSyncApiResponse;
      return healthSyncMatchesDate(parsed, requestDate) ? parsed : null;
    } catch {
      return null;
    }
  }

  saveCachedHealthSync(payload: HealthSyncApiResponse, date?: string): void {
    const requestDate =
      date ||
      payload.date_local ||
      payload.selected_date ||
      payload.today_local ||
      payload.today_overview?.date ||
      getLocalDateString();
    try {
      localStorage.setItem(cacheKeyForDate(requestDate), JSON.stringify(payload));
      localStorage.setItem(HEALTH_SYNC_API_CACHE_KEY, JSON.stringify(payload));
    } catch {
      // quota / private mode
    }
  }

  loadCachedPayload(): GoogleHealthPayload | null {
    try {
      const raw = localStorage.getItem(GOOGLE_HEALTH_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as GoogleHealthPayload;
      return Array.isArray(parsed?.health_data) ? parsed : null;
    } catch {
      return null;
    }
  }

  saveCachedPayload(payload: GoogleHealthPayload): void {
    try {
      localStorage.setItem(GOOGLE_HEALTH_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // quota / private mode
    }
  }

  clearCachedPayload(): void {
    try {
      localStorage.removeItem(GOOGLE_HEALTH_STORAGE_KEY);
      localStorage.removeItem(HEALTH_SYNC_API_CACHE_KEY);
    } catch {
      // ignore
    }
  }

  /** GET synced health trends from backend. Always sends local YYYY-MM-DD. */
  async fetchHealthSync(date?: string): Promise<HealthSyncApiResponse | null> {
    const requestDate = date || getLocalDateString();
    const url = `${HEALTH_SYNC_API_PATH}?date=${encodeURIComponent(requestDate)}`;

    const response = await get<HealthSyncApiResponse>(url, healthSyncRequestConfig());

    if (!response || typeof response !== "object") return null;
    return response;
  }

  /** POST Flutter health_data (+ optional steps_goal / calories_goal) to backend. */
  async syncToBackend(
    payload: GoogleHealthPayload,
    options?: { date?: string; goals?: HealthSyncGoals },
  ): Promise<{ ok: boolean; error?: string; response?: HealthSyncApiResponse }> {
    const userId = resolveUserId(payload);
    if (!userId) {
      return { ok: false, error: "User not logged in" };
    }

    const requestDate = options?.date || getLocalDateString();
    const hasGoals =
      (options?.goals?.steps_goal != null && options.goals.steps_goal > 0) ||
      (options?.goals?.calories_goal != null && options.goals.calories_goal > 0);

    const rows = payload.health_data?.length
      ? payload.health_data
      : hasGoals
        ? [
            {
              date: requestDate,
              steps: 0,
              calories: 0,
              distance: 0,
              sleep_minutes: 0,
              spo2: null,
              hrv: null,
              breathing_rate: null,
              skin_temperature: null,
            } satisfies GoogleHealthDayRow,
          ]
        : [];

    if (!rows.length) {
      return { ok: false, error: "No health data to save" };
    }

    const body: GoogleHealthSyncRequest = {
      user_id: userId,
      health_data: enrichHealthDataWithGoals(rows, options?.goals, requestDate),
    };

    const url = `${HEALTH_SYNC_API_PATH}?date=${encodeURIComponent(requestDate)}`;

    const response = await post<HealthSyncApiResponse>(
      url,
      body,
      healthSyncRequestConfig(),
    );

    if (response == null) {
      return { ok: false, error: "Could not save health data to server" };
    }

    return { ok: true, response };
  }
}

export default GoogleHealthService.getInstance();
